import React, { useRef, useState } from 'react';
import Alert from 'react-s-alert';

import { Avatar, Box, Heading, ConfirmModal, Icon } from 'components';
import { Link } from 'core/routing';
import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';

interface CollaboratorsOverviewProps {
  collaborators: {
    profile: {
      username?: string;
      avatarUrl?: string;
      participantDisplayName?: string;
      imageFallbackChar: string;
      fullName: string;
      projects?: number;
      sharedFiles?: number;
    };
  }[];
  onRemoveCollaborators: (
    payload: {
      spaceId: string;
      userName: string;
    },
    callbacks: {
      onSuccess: () => void;
      onUserErr: (error: any) => void;
      onSysErr: (error: any) => void;
    },
  ) => void;
  onRequeryCollaborators: (slug: string) => void;
  className?: string;
}

export const CollaboratorsOverview = (props: CollaboratorsOverviewProps) => {
  const {
    collaborators,
    className,
    onRemoveCollaborators,
    onRequeryCollaborators,
  } = props;
  const removedUserRef = useRef('');
  const [hoverUsername, setHoverUsername] = useState('');
  const [loading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const closeConfirmModal = () => {
    setHoverUsername('');
    setOpen(false);
  };

  const errorMessage = 'There has been an error while removing this member.';
  const handleConfirmModal = () => {
    setIsLoading(true);
    const currentSpaceIdOfInitiative = clientStore.currentSpaceData.inSpace.id;
    onRemoveCollaborators(
      {
        spaceId: currentSpaceIdOfInitiative,
        userName: removedUserRef.current,
      },
      {
        onSuccess: () => {
          closeConfirmModal();
          setIsLoading(false);
          removedUserRef.current = '';
          onRequeryCollaborators(
            clientStore.routerStore.route.params.spacePath,
          );
        },
        onUserErr: error => {
          setIsLoading(false);
          analytics.logError(error);
          Alert.error(errorMessage);
        },
        onSysErr: error => {
          setIsLoading(false);
          analytics.logError(error);
          Alert.error(errorMessage);
        },
      },
    );
  };

  return (
    <div className={`overflow-y-auto overflow-x-hidden ${className}`}>
      {collaborators.map((collaborator, index) => {
        const {
          username,
          avatarUrl,
          fullName,
          participantDisplayName,
          imageFallbackChar,
        } = collaborator?.profile || {};

        return (
          <div
            className={`flex items-center gap-4 ${index === 0 ? '' : 'mt-4'}`}
            key={username}
            data-testid={`outside-collaborator-${username}`}
            onMouseEnter={() => {
              setHoverUsername(username);
              removedUserRef.current = username;
            }}
            onMouseLeave={() => setHoverUsername('')}
          >
            <div className='flex'>
              <Link name='profile' params={{ profile: username }}>
                <Avatar
                  name={imageFallbackChar}
                  src={avatarUrl}
                  size='medium'
                />
              </Link>
              <Box
                ml={2}
                sx={{
                  flexDirection: 'column',
                  display: 'flex',
                  flex: 1,
                }}
              >
                <Heading size='xsmall'>{fullName}</Heading>
                <span className='font-light text-xs text-black'>
                  {username || participantDisplayName}
                </span>
              </Box>
            </div>

            {hoverUsername === username && (
              <Icon
                outline
                size='small'
                name='close'
                variant='destructive'
                onClick={() => {
                  setOpen(true);
                  setHoverUsername(username);
                }}
                className=' cursor-pointer text-wfRed hover:opacity-70'
                data-testid={`MembersPage-removeOutsideCollaborators${username}Btn`}
              />
            )}
          </div>
        );
      })}
      <ConfirmModal
        open={open}
        loading={loading}
        content={
          <div className='flex flex-col justify-center items-center'>
            <div className=' text-wfBlack my-2 w-full'>
              Remove this outside collaborator will result of lose access
            </div>
          </div>
        }
        onConfirm={handleConfirmModal}
        onClose={closeConfirmModal}
      />
    </div>
  );
};
